import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import jsPDF from 'jspdf';
import './AdminReports.css';

interface ReportData {
  intensity: string;
  count: number;
  date: string;
}

interface ChartData {
  label: string;
  value: number;
  color: string;
}

const AdminReports: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [summaryData, setSummaryData] = useState<ChartData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [reportType, setReportType] = useState<'intensity' | 'daily' | 'monthly'>('intensity');

  useEffect(() => {
    // Set default date range to last 30 days
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setDateRange({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });
  }, []);

  const generateReport = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await adminAPI.getIntensityReport(
        dateRange.startDate && dateRange.endDate ? dateRange : undefined
      );

      const data = response.data.data.report;
      setReportData(data);

      // Process data for charts
      const intensityCounts: Record<string, number> = { leve: 0, moderada: 0, fuerte: 0 };
      data.forEach((item: ReportData) => {
        if (intensityCounts[item.intensity] !== undefined) {
          intensityCounts[item.intensity] += item.count;
        }
      });

      const colors: Record<string, string> = {
        leve: '#10b981',
        moderada: '#f59e0b',
        fuerte: '#ef4444'
      };

      const chartData: ChartData[] = Object.entries(intensityCounts).map(([label, value]) => ({
        label: label.charAt(0).toUpperCase() + label.slice(1),
        value,
        color: colors[label] || '#6b7280'
      }));

      setSummaryData(chartData);
    } catch (err: any) {
      setError('Error al generar reporte');
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (reportData.length === 0) return;

    const headers = ['Fecha', 'Intensidad', 'Cantidad'];
    const rows = reportData.map(item => [
      new Date(item.date).toLocaleDateString('es-ES'),
      item.intensity,
      item.count.toString()
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `reporte_intensidad_${dateRange.startDate}_${dateRange.endDate}.csv`;
    link.click();
  };

  const exportToPDF = () => {
    if (reportData.length === 0) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(20);
    doc.setTextColor(99, 102, 241);
    doc.text('MalDolor - Reporte de Intensidad', pageWidth / 2, 20, { align: 'center' });

    // Date range
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(`Periodo: ${dateRange.startDate} a ${dateRange.endDate}`, pageWidth / 2, 30, { align: 'center' });

    // Summary
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text('Resumen:', 20, 45);

    let yPos = 55;
    const total = summaryData.reduce((acc, item) => acc + item.value, 0);

    summaryData.forEach((item) => {
      const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
      doc.setFontSize(11);
      doc.text(`${item.label}: ${item.value} registros (${percentage}%)`, 25, yPos);
      yPos += 8;
    });

    yPos += 10;
    doc.text(`Total: ${total} registros`, 25, yPos);

    // Table header
    yPos += 15;
    doc.setFillColor(99, 102, 241);
    doc.rect(20, yPos - 5, pageWidth - 40, 10, 'F');
    doc.setTextColor(255);
    doc.setFontSize(10);
    doc.text('Fecha', 25, yPos + 2);
    doc.text('Intensidad', 80, yPos + 2);
    doc.text('Cantidad', 140, yPos + 2);

    // Table rows
    doc.setTextColor(0);
    yPos += 12;

    reportData.slice(0, 30).forEach((item) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(new Date(item.date).toLocaleDateString('es-ES'), 25, yPos);
      doc.text(item.intensity, 80, yPos);
      doc.text(item.count.toString(), 140, yPos);
      yPos += 7;
    });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Generado el ${new Date().toLocaleString('es-ES')}`, pageWidth / 2, 290, { align: 'center' });

    doc.save(`reporte_intensidad_${dateRange.startDate}_${dateRange.endDate}.pdf`);
  };

  const totalCount = summaryData.reduce((acc, item) => acc + item.value, 0);
  const maxValue = Math.max(...summaryData.map(item => item.value), 1);

  return (
    <div className="admin-reports">
      <div className="reports-header">
        <h1>Reportes y Analisis</h1>
        <p>Genera reportes detallados de la actividad del sistema</p>
      </div>

      <div className="report-controls">
        <div className="control-group">
          <label>Tipo de reporte:</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value as any)}
            className="report-select"
          >
            <option value="intensity">Por intensidad</option>
            <option value="daily">Diario</option>
            <option value="monthly">Mensual</option>
          </select>
        </div>

        <div className="date-range">
          <div className="control-group">
            <label>Fecha inicio:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="date-input"
            />
          </div>
          <div className="control-group">
            <label>Fecha fin:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="date-input"
            />
          </div>
        </div>

        <button
          onClick={generateReport}
          disabled={isLoading}
          className="btn btn-primary"
        >
          {isLoading ? 'Generando...' : 'Generar Reporte'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {summaryData.length > 0 && (
        <>
          {/* Chart Section */}
          <div className="charts-section">
            <div className="chart-card">
              <h3>Distribucion por Intensidad</h3>
              <div className="bar-chart">
                {summaryData.map((item) => (
                  <div key={item.label} className="bar-item">
                    <div className="bar-label-row">
                      <span className="bar-name">{item.label}</span>
                      <span className="bar-value">{item.value}</span>
                    </div>
                    <div className="bar-container">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${(item.value / maxValue) * 100}%`,
                          backgroundColor: item.color
                        }}
                      ></div>
                    </div>
                    <span className="bar-percentage">
                      {totalCount > 0 ? ((item.value / totalCount) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="chart-card">
              <h3>Grafico Circular</h3>
              <div className="pie-chart-container">
                <svg viewBox="0 0 100 100" className="pie-chart">
                  {(() => {
                    let cumulativePercent = 0;
                    return summaryData.map((item, index) => {
                      const percent = totalCount > 0 ? (item.value / totalCount) * 100 : 0;
                      const startAngle = cumulativePercent * 3.6;
                      cumulativePercent += percent;
                      const endAngle = cumulativePercent * 3.6;

                      const startRad = (startAngle - 90) * Math.PI / 180;
                      const endRad = (endAngle - 90) * Math.PI / 180;

                      const x1 = 50 + 40 * Math.cos(startRad);
                      const y1 = 50 + 40 * Math.sin(startRad);
                      const x2 = 50 + 40 * Math.cos(endRad);
                      const y2 = 50 + 40 * Math.sin(endRad);

                      const largeArc = percent > 50 ? 1 : 0;

                      const pathData = percent >= 100
                        ? `M 50 10 A 40 40 0 1 1 49.99 10 Z`
                        : `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;

                      return (
                        <path
                          key={index}
                          d={pathData}
                          fill={item.color}
                          stroke="white"
                          strokeWidth="1"
                        />
                      );
                    });
                  })()}
                </svg>
                <div className="pie-legend">
                  {summaryData.map((item) => (
                    <div key={item.label} className="legend-item">
                      <span className="legend-color" style={{ backgroundColor: item.color }}></span>
                      <span className="legend-label">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="summary-stats">
            <div className="stat-card">
              <span className="stat-number">{totalCount}</span>
              <span className="stat-title">Total Registros</span>
            </div>
            {summaryData.map((item) => (
              <div key={item.label} className="stat-card" style={{ borderColor: item.color }}>
                <span className="stat-number" style={{ color: item.color }}>{item.value}</span>
                <span className="stat-title">{item.label}</span>
              </div>
            ))}
          </div>

          {/* Export Buttons */}
          <div className="export-actions">
            <button onClick={exportToCSV} className="btn btn-export">
              📊 Exportar CSV
            </button>
            <button onClick={exportToPDF} className="btn btn-export btn-pdf">
              📄 Exportar PDF
            </button>
          </div>

          {/* Data Table */}
          <div className="report-results">
            <h2>Detalle por Fecha</h2>
            <div className="table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Intensidad</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((item, index) => (
                    <tr key={index}>
                      <td>{new Date(item.date).toLocaleDateString('es-ES')}</td>
                      <td>
                        <span
                          className="intensity-badge"
                          style={{
                            backgroundColor: `${item.intensity === 'leve' ? '#10b981' :
                              item.intensity === 'moderada' ? '#f59e0b' : '#ef4444'}20`,
                            color: item.intensity === 'leve' ? '#10b981' :
                              item.intensity === 'moderada' ? '#f59e0b' : '#ef4444'
                          }}
                        >
                          {item.intensity}
                        </span>
                      </td>
                      <td>{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {reportData.length === 0 && !isLoading && (
        <div className="empty-state">
          <span className="empty-icon">📊</span>
          <p>Selecciona un rango de fechas y genera un reporte</p>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
